// Constants
export const DEFAULT_LANGUAGE = 'en';
export const DEFAULT_CATEGORY = 'UTILITY';
export const COMPONENT_TYPES = {
  HEADER: 'HEADER',
  BODY: 'BODY',
  BUTTONS: 'BUTTONS',
};
export const MEDIA_FORMATS = ['IMAGE', 'VIDEO', 'DOCUMENT'];

export const findComponentByType = (template, type) =>
  template.components?.find(component => component.type === type);

export const processVariable = str => {
  return str.replace(/{{|}}/g, '');
};

// The document filename only travels when given (the server adds it if present),
// so it is the one template field that may stay empty.
const OPTIONAL_KEYS = ['media_name'];

// Walks every level: the parser hands over { body, header, buttons }, and the
// empty values live inside them. Looking only at the first level let `{{nome}}`
// and the header media_url go out empty, and Meta refused both (CRM-04 C2).
export const allKeysRequired = value => {
  return Object.entries(value).every(([key, item]) => {
    if (OPTIONAL_KEYS.includes(key)) return true;
    // The button's index in the template, not something typed: the first is 0.
    if (typeof item === 'number') return true;
    if (item && typeof item === 'object') return allKeysRequired(item);
    return Boolean(item);
  });
};

// The first parameter still empty, in the order the form shows them: the media
// link, the variables, then the buttons, counted from 1 like the template. The
// same reading the server refusal gives (CRM-04 C2), so the screen can name the
// field instead of "fill in all the variables" when it is the media link.
const MEDIA_KEYS = ['media_url', 'media_type', ...OPTIONAL_KEYS];

export const firstEmptyParameter = (processedParams = {}) => {
  const { header, buttons } = processedParams;
  if (
    header &&
    ('media_url' in header || header.media_type) &&
    !header.media_url
  )
    return { type: 'media' };

  const variable = ['header', 'body', 'footer']
    .flatMap(section => Object.entries(processedParams[section] || {}))
    .find(([key, value]) => !MEDIA_KEYS.includes(key) && !value);
  if (variable) return { type: 'variable', name: variable[0] };

  // findIndex visits the holes of a sparse list, so the position is the button's
  // place in the template; the index it carries says the same, and is what counts.
  const position = (buttons || []).findIndex(
    button => button && !button.parameter
  );
  if (position !== -1) {
    const { index = position } = buttons[position];
    return { type: 'button', number: index + 1 };
  }
  return null;
};

export const replaceTemplateVariables = (templateText, processedParams) => {
  return templateText.replace(/{{([^}]+)}}/g, (match, variable) => {
    const variableKey = processVariable(variable);
    return processedParams.body?.[variableKey] || `{{${variable}}}`;
  });
};

export const buildTemplateParameters = (template, hasMediaHeaderValue) => {
  const allVariables = {};

  const bodyComponent = findComponentByType(template, COMPONENT_TYPES.BODY);
  const headerComponent = findComponentByType(template, COMPONENT_TYPES.HEADER);

  if (!bodyComponent) return allVariables;

  const templateString = bodyComponent.text;

  // Process body variables
  const matchedVariables = templateString.match(/{{([^}]+)}}/g);
  if (matchedVariables) {
    allVariables.body = {};
    matchedVariables.forEach(variable => {
      const key = processVariable(variable);
      allVariables.body[key] = '';
    });
  }

  if (hasMediaHeaderValue) {
    if (!allVariables.header) allVariables.header = {};
    allVariables.header.media_url = '';
    allVariables.header.media_type = headerComponent.format.toLowerCase();

    // For document templates, include media_name field for filename support
    if (headerComponent.format.toLowerCase() === 'document') {
      allVariables.header.media_name = '';
    }
  }

  // Process button variables. Each one sits at its place in the template, with a
  // hole where a button takes no parameter (a quick reply before a URL), and also
  // carries that place as `index`: the hole does not survive the trip — Rails drops
  // the null from the params, the next button slides into it, and the parameter
  // went to the wrong button. The server reads the `index`.
  const buttonComponents = template.components.filter(
    component => component.type === COMPONENT_TYPES.BUTTONS
  );

  buttonComponents.forEach(buttonComponent => {
    if (buttonComponent.buttons) {
      buttonComponent.buttons.forEach((button, index) => {
        // Handle URL buttons with variables
        if (button.type === 'URL' && button.url && button.url.includes('{{')) {
          const buttonVars = button.url.match(/{{([^}]+)}}/g) || [];
          if (buttonVars.length > 0) {
            if (!allVariables.buttons) allVariables.buttons = [];
            allVariables.buttons[index] = {
              type: 'url',
              parameter: '',
              url: button.url,
              variables: buttonVars.map(v => processVariable(v)),
              index,
            };
          }
        }

        // Handle copy code buttons
        if (button.type === 'COPY_CODE') {
          if (!allVariables.buttons) allVariables.buttons = [];
          allVariables.buttons[index] = {
            type: 'copy_code',
            parameter: '',
            index,
          };
        }
      });
    }
  });

  return allVariables;
};
